import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import {
  createClinic,
  deleteClinic,
  listClinics,
  patchClinic
} from '../api/clinicsApi';
import { normalizeUiLocale } from '../utils/locale';

const emptyClinicForm = {
  name: '',
  status: 'CLOSED',
  token: ''
};

const pageSizeOptions = [10, 20, 50, 100];
const statusOptions = ['CLOSED', 'OPEN', 'BUSY'];

const getRequestErrorMessage = (error, fallback) => {
  return error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
};

const getNullableID = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    if (value.Valid === false || value.valid === false) {
      return null;
    }
    return value.Int64 ?? value.int64 ?? value.Int ?? value.int ?? value.value ?? null;
  }

  return null;
};

const formatDateTime = (value, locale) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString(locale);
};

const ClinicManagement = () => {
  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [editingClinicId, setEditingClinicId] = useState(null);
  const [form, setForm] = useState(emptyClinicForm);
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    clinicId: null,
    clinicName: ''
  });

  const selectedClinic = useMemo(
    () => clinics.find((clinic) => clinic.id === editingClinicId) || null,
    [clinics, editingClinicId]
  );

  const loadClinics = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listClinics({ limit: 1000, offset: 0 });
      setClinics(Array.isArray(data) ? data : []);
    } catch (requestError) {
      console.error('Failed to load clinics:', requestError);
      setError(getRequestErrorMessage(requestError, t('clinics.fetchError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClinics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showForm || saving) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        resetForm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, saving]);

  const filteredClinics = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return clinics.filter((clinic) => {
      const matchesStatus = statusFilter === 'all' || clinic.status === statusFilter;
      if (!matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const currentPatientId = getNullableID(clinic.current_patient_id);
      const currentVisitId = getNullableID(clinic.current_visit_id);
      const searchable = [
        clinic.id,
        clinic.name,
        clinic.secret,
        clinic.status,
        currentPatientId,
        currentVisitId
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(' ')
        .toLowerCase();

      return searchable.includes(normalizedSearch);
    });
  }, [clinics, searchValue, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredClinics.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pagedClinics = filteredClinics.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);

  useEffect(() => {
    if (currentPage !== safeCurrentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [currentPage, safeCurrentPage]);

  const statusCounts = useMemo(() => {
    return clinics.reduce(
      (accumulator, clinic) => ({
        ...accumulator,
        [clinic.status]: (accumulator[clinic.status] || 0) + 1
      }),
      { OPEN: 0, BUSY: 0, CLOSED: 0 }
    );
  }, [clinics]);

  const resetForm = () => {
    setEditingClinicId(null);
    setForm(emptyClinicForm);
    setShowForm(false);
  };

  const beginCreate = () => {
    setError('');
    setNotice('');
    setEditingClinicId(null);
    setForm(emptyClinicForm);
    setShowForm(true);
  };

  const beginEdit = (clinic) => {
    setError('');
    setNotice('');
    setEditingClinicId(clinic.id);
    setForm({
      name: clinic.name || '',
      status: clinic.status || 'CLOSED',
      token: clinic.secret || ''
    });
    setShowForm(true);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      setError(t('clinics.requiredFields'));
      return false;
    }

    if (!form.token.trim()) {
      setError(t('clinics.tokenRequired'));
      return false;
    }

    const tokenValue = form.token.trim();
    const duplicateClinic = clinics.find((clinic) => (
      clinic.secret === tokenValue && clinic.id !== editingClinicId
    ));
    if (duplicateClinic) {
      setError(t('clinics.tokenDuplicate', { name: duplicateClinic.name || `#${duplicateClinic.id}` }));
      return false;
    }

    if (!statusOptions.includes(form.status)) {
      setError(t('clinics.invalidStatus'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setNotice('');

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = {
        name: form.name.trim(),
        status: form.status,
        secret: form.token.trim()
      };

      if (editingClinicId) {
        await patchClinic(editingClinicId, payload);
        setNotice(t('clinics.updateSuccess'));
      } else {
        await createClinic(payload);
        setNotice(t('clinics.createSuccess'));
      }

      resetForm();
      await loadClinics();
    } catch (requestError) {
      console.error('Failed to save clinic:', requestError);
      const fallback = requestError?.response?.status === 409
        ? t('clinics.tokenDuplicateGeneric')
        : t('clinics.saveError');
      setError(getRequestErrorMessage(requestError, fallback));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (clinic) => {
    setDeleteConfirm({
      show: true,
      clinicId: clinic.id,
      clinicName: clinic.name || `#${clinic.id}`
    });
  };

  const confirmDelete = async () => {
    try {
      setNotice('');
      setError('');
      await deleteClinic(deleteConfirm.clinicId);
      setDeleteConfirm({ show: false, clinicId: null, clinicName: '' });
      setNotice(t('clinics.deleteSuccess'));
      await loadClinics();
    } catch (requestError) {
      console.error('Failed to delete clinic:', requestError);
      setError(getRequestErrorMessage(requestError, t('clinics.deleteError')));
      setDeleteConfirm({ show: false, clinicId: null, clinicName: '' });
    }
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchValue('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const getStatusLabel = (status) => t(`clinics.status${status || 'Unknown'}`);

  const renderStatusPill = (status) => {
    if (status === 'OPEN') {
      return <span className="status-pill status-pill--success">{getStatusLabel(status)}</span>;
    }
    if (status === 'BUSY') {
      return <span className="status-pill status-pill--warning">{getStatusLabel(status)}</span>;
    }
    if (status === 'CLOSED') {
      return <span className="status-pill status-pill--neutral">{getStatusLabel(status)}</span>;
    }
    return <span className="status-pill status-pill--neutral">{t('common.unknown')}</span>;
  };

  return (
    <div className="admin-page">
      <div className="page-header admin-header-panel">
        <div className="page-title-block">
          <h1 className="page-title">{t('clinics.title')}</h1>
        </div>

        <div className="page-actions">
          <button type="button" className="theme-button theme-button--primary" onClick={beginCreate}>
            {t('clinics.newClinic')}
          </button>
          <button type="button" className="theme-button theme-button--ghost" onClick={loadClinics} disabled={loading}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {error && !showForm && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className="status-banner status-banner--success">
          <span>{notice}</span>
        </div>
      )}

      <section className="surface-panel admin-panel">
        <div className="surface-panel__body stack">
          <div className="admin-section-header">
            <div className="admin-stat-strip">
              <span className="status-pill status-pill--cool">
                {t('clinics.totalSummary', { count: clinics.length })}
              </span>
              <span className="status-pill status-pill--success">
                {t('clinics.openSummary', { count: statusCounts.OPEN || 0 })}
              </span>
              <span className="status-pill status-pill--warning">
                {t('clinics.busySummary', { count: statusCounts.BUSY || 0 })}
              </span>
              <span className="status-pill status-pill--neutral">
                {t('clinics.closedSummary', { count: statusCounts.CLOSED || 0 })}
              </span>
            </div>
          </div>

          <div className="filter-row clinic-filter-row">
            <div className="field">
              <label className="field__label" htmlFor="clinic-status-filter">{t('clinics.statusFilter')}</label>
              <select
                id="clinic-status-filter"
                className="theme-select"
                value={statusFilter}
                onChange={(event) => handleStatusFilterChange(event.target.value)}
              >
                <option value="all">{t('clinics.allStatuses')}</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="clinic-search">{t('clinics.search')}</label>
              <input
                id="clinic-search"
                className="theme-input"
                type="search"
                value={searchValue}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder={t('clinics.searchPlaceholder')}
              />
            </div>

            <button type="button" className="theme-button theme-button--secondary" onClick={resetFilters}>
              {t('common.reset')}
            </button>
            <button type="button" className="theme-button theme-button--ghost" onClick={loadClinics} disabled={loading}>
              {t('common.refresh')}
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : filteredClinics.length === 0 ? (
            <div className="empty-state">
              <h3>{t('clinics.emptyTitle')}</h3>
              <p>{t('clinics.emptyHint')}</p>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('clinics.clinic')}</th>
                    <th>{t('clinics.token')}</th>
                    <th>{t('clinics.status')}</th>
                    <th>{t('clinics.currentPatientId')}</th>
                    <th>{t('clinics.currentVisitId')}</th>
                    <th>{t('clinics.createdAt')}</th>
                    <th>{t('clinics.updatedAt')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedClinics.map((clinic) => {
                    const currentPatientId = getNullableID(clinic.current_patient_id);
                    const currentVisitId = getNullableID(clinic.current_visit_id);

                    return (
                      <tr key={clinic.id}>
                        <td>
                          <div className="table-title">{clinic.name || t('clinics.unnamedClinic')}</div>
                          <div className="table-meta">#{clinic.id}</div>
                        </td>
                        <td>
                          <div className="table-token">{clinic.secret || '-'}</div>
                        </td>
                        <td>{renderStatusPill(clinic.status)}</td>
                        <td>{currentPatientId || '-'}</td>
                        <td>{currentVisitId || '-'}</td>
                        <td>{formatDateTime(clinic.created_at, locale)}</td>
                        <td>{formatDateTime(clinic.updated_at, locale)}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              type="button"
                              className="theme-button theme-button--ghost"
                              onClick={() => beginEdit(clinic)}
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              type="button"
                              className="theme-button theme-button--danger"
                              onClick={() => handleDelete(clinic)}
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Pagination
                currentPage={safeCurrentPage}
                totalPages={totalPages}
                totalCount={filteredClinics.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
              />
            </div>
          )}
        </div>
      </section>

      {showForm && (
        <div className="dialog-backdrop">
          <div
            className="dialog-shell admin-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="clinic-form-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="page-title-block">
              <h2 id="clinic-form-title" className="dialog-shell__title">
                {editingClinicId ? selectedClinic?.name || t('clinics.editClinic') : t('clinics.createClinic')}
              </h2>
            </div>

            {error && (
              <div className="status-banner status-banner--error">
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="stack">
              <div className="form-grid form-grid--single">
                <div className="field">
                  <label className="field__label" htmlFor="clinic-name">{t('clinics.name')}</label>
                  <input
                    id="clinic-name"
                    className="theme-input"
                    type="text"
                    value={form.name}
                    onChange={(event) => handleFieldChange('name', event.target.value)}
                    placeholder={t('clinics.namePlaceholder')}
                    autoFocus
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="clinic-status">{t('clinics.status')}</label>
                  <select
                    id="clinic-status"
                    className="theme-select"
                    value={form.status}
                    onChange={(event) => handleFieldChange('status', event.target.value)}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {getStatusLabel(status)}
                      </option>
                    ))}
                  </select>
                  <span className="field__hint">{t('clinics.statusHint')}</span>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="clinic-token">{t('clinics.token')}</label>
                  <input
                    id="clinic-token"
                    className="theme-input"
                    type="text"
                    value={form.token}
                    onChange={(event) => handleFieldChange('token', event.target.value)}
                    placeholder={t('clinics.tokenPlaceholder')}
                  />
                  <span className="field__hint">{t('clinics.tokenHint')}</span>
                </div>
              </div>

              <div className="dialog-shell__actions">
                <button type="button" className="theme-button theme-button--ghost" onClick={resetForm} disabled={saving}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="theme-button theme-button--primary" disabled={saving}>
                  {saving ? t('common.submitting') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title={t('clinics.confirmDelete')}
        message={t('clinics.confirmDeleteMessage', { name: deleteConfirm.clinicName })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDelete}
        onClose={() => setDeleteConfirm({ show: false, clinicId: null, clinicName: '' })}
        type="danger"
      />
    </div>
  );
};

export default ClinicManagement;
